/**
 * MobX store for user metadata management.
 * Uses legacy FHMetaData/FHMetaDataRequests via datastore keys.
 */
import { makeAutoObservable, runInAction } from 'mobx';
import { userMetadataApi } from '../api/user_metadata_api';
import type { MetadataType, UserMetadata } from '../api/user_metadata_api';

export class UserMetadataStore {
  // Metadata types (cached)
  metadataTypes: MetadataType[] = [];
  metadataTypesLoaded = false;
  metadataTypesLoading = false;

  // User metadata by user key (ref)
  metadataByRef: Map<string, UserMetadata[]> = new Map();

  // Loading states
  loadingByRef: Map<string, boolean> = new Map();

  // Operation states
  creating = false;
  updating = false;
  removing = false;

  constructor() {
    makeAutoObservable(this);
  }

  /**
   * Fetch all accepted metadata types.
   */
  async fetchMetadataTypes(): Promise<void> {
    if (this.metadataTypesLoaded || this.metadataTypesLoading) return;

    this.metadataTypesLoading = true;
    try {
      const response = await userMetadataApi.getMetadataTypes();
      runInAction(() => {
        this.metadataTypes = response.results;
        this.metadataTypesLoaded = true;
      });
    } catch (error) {
      console.error('Failed to fetch metadata types:', error);
    } finally {
      runInAction(() => {
        this.metadataTypesLoading = false;
      });
    }
  }

  /**
   * Get metadata types (triggers fetch if not loaded).
   */
  getMetadataTypes(): MetadataType[] {
    if (!this.metadataTypesLoaded && !this.metadataTypesLoading) {
      this.fetchMetadataTypes();
    }
    return this.metadataTypes;
  }

  /**
   * Fetch metadata for a specific user (by ref key).
   */
  async fetchByRef(refKey: string): Promise<void> {
    if (this.loadingByRef.get(refKey)) return;

    this.loadingByRef.set(refKey, true);
    try {
      const response = await userMetadataApi.listByRef(refKey);
      runInAction(() => {
        this.metadataByRef.set(refKey, response.results);
      });
    } catch (error) {
      console.error('Failed to fetch user metadata:', error);
    } finally {
      runInAction(() => {
        this.loadingByRef.set(refKey, false);
      });
    }
  }

  /**
   * Get metadata for a user (returns cached data).
   */
  getByRef(refKey: string): UserMetadata[] {
    return this.metadataByRef.get(refKey) || [];
  }

  /**
   * Check if loading metadata for a user.
   */
  isLoadingByRef(refKey: string): boolean {
    return this.loadingByRef.get(refKey) || false;
  }

  /**
   * Create a new metadata entry.
   */
  async createMetadata(params: {
    ref: string; // user key
    metadata_request: string; // metadata type key
    value: string | number | boolean;
  }): Promise<UserMetadata | null> {
    this.creating = true;
    try {
      const metadata = await userMetadataApi.createUserMetadata(params);
      runInAction(() => {
        // Add to user's metadata
        const userMetadata = this.metadataByRef.get(params.ref) || [];
        this.metadataByRef.set(params.ref, [metadata, ...userMetadata]);
      });
      return metadata;
    } catch (error) {
      console.error('Failed to create metadata:', error);
      return null;
    } finally {
      runInAction(() => {
        this.creating = false;
      });
    }
  }

  /**
   * Update an existing metadata entry.
   */
  async updateMetadata(params: {
    key: string;
    value: string | number | boolean;
  }): Promise<UserMetadata | null> {
    this.updating = true;
    try {
      const metadata = await userMetadataApi.updateUserMetadata(params);
      runInAction(() => {
        // Update in user's metadata
        const refKey = metadata.ref;
        const userMetadata = this.metadataByRef.get(refKey);
        if (userMetadata) {
          const index = userMetadata.findIndex((m) => m.key === params.key);
          if (index !== -1) {
            userMetadata[index] = metadata;
          }
        }
      });
      return metadata;
    } catch (error) {
      console.error('Failed to update metadata:', error);
      return null;
    } finally {
      runInAction(() => {
        this.updating = false;
      });
    }
  }

  /**
   * Remove (soft-delete) a metadata entry.
   */
  async removeMetadata(key: string, refKey: string): Promise<boolean> {
    this.removing = true;
    try {
      await userMetadataApi.removeUserMetadata(key);
      runInAction(() => {
        // Remove from user's metadata
        const userMetadata = this.metadataByRef.get(refKey);
        if (userMetadata) {
          this.metadataByRef.set(
            refKey,
            userMetadata.filter((m) => m.key !== key)
          );
        }
      });
      return true;
    } catch (error) {
      console.error('Failed to remove metadata:', error);
      return false;
    } finally {
      runInAction(() => {
        this.removing = false;
      });
    }
  }

  /**
   * Clear cached metadata for a user.
   */
  clearRefCache(refKey: string): void {
    this.metadataByRef.delete(refKey);
  }
}
